import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { toast } from "react-toastify";
import {
  getClickStats,
  ClickStatsResponse,
} from "../services/menuClick_service";
import LoadingSpinner from "./LoadingSpinner";
import "../css/ClientConnectionStatsPanel.css";
import HourlyConnectionChart from "./Hourlyconnectionchart";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  selectedPeriod:        string;
  customFromDate:        string;
  customToDate:          string;
  selectedUserType:      string;
  selectedAccountStatus: string;
}

interface HourlyChartPoint {
  intervalle: string;
  connectes:  number;
}

interface DailyPeak {
  date:      string;
  connected: number;
}

const RECHARTS_COLORS = {
  teal:        "#2A919D",
  trackTeal:   "#daeef0",
  orange:      "#eb8635",
  trackOrange: "#fde8d4",
} as const;

// ─── DonutGauge ──────────────────────────────────────────────────────────────

interface DonutProps {
  value:      number;
  peak:       number;
  fillColor:  string;
  trackColor: string;
  variant:    "teal" | "orange";
  label:      string;
  sublabel:   string;
}

function DonutGauge({ value, peak, fillColor, trackColor, variant, label, sublabel }: DonutProps) {
  // Arc fill still based on proportion — only the centre text changes to raw value
  const pct  = peak > 0 ? Math.min(Math.round((value / peak) * 100), 100) : 0;
  const data = [{ v: pct }, { v: 100 - pct }];

  return (
    <div className="ccsp-gauge">
      <div className="ccsp-gauge__ring-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%" cy="50%"
              innerRadius={52} outerRadius={72}
              startAngle={90} endAngle={-270}
              dataKey="v"
              strokeWidth={0}
              paddingAngle={pct > 0 && pct < 100 ? 2 : 0}
            >
              <Cell fill={fillColor} />
              <Cell fill={trackColor} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Centre : raw count instead of percentage */}
        <div className="ccsp-gauge__center">
          <span className="ccsp-gauge__count" style={{ color: fillColor }}>
            {value.toLocaleString("fr-FR")}
          </span>
        </div>

        <div className={`ccsp-gauge__glow ccsp-gauge__glow--${variant}`} />
      </div>

      <div className="ccsp-gauge__label-block">
        <div className="ccsp-gauge__label-row">
          <span className={`ccsp-gauge__dot ccsp-gauge__dot--${variant}`} />
          <span className="ccsp-gauge__label-text">{label}</span>
        </div>
        <div className="ccsp-gauge__sublabel">{sublabel}</div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ClientConnectionStatsPanel({
  selectedPeriod,
  customFromDate,
  customToDate,
  selectedUserType,
  selectedAccountStatus,
}: Props) {
  const [clickStats, setClickStats] = useState<ClickStatsResponse | null>(null);
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    let ignore = false;

    const fetch = async () => {
      setLoading(true);
      try {
        const response = await getClickStats({
          period:   selectedPeriod || "30d",
          from:     selectedPeriod === "custom" ? customFromDate || undefined : undefined,
          to:       selectedPeriod === "custom" ? customToDate   || undefined : undefined,
          userType: selectedUserType       || undefined,
          status:   selectedAccountStatus || undefined,
        });
        if (!ignore) setClickStats(response);
      } catch (error) {
        console.error("Erreur chargement stats de clics:", error);
        if (!ignore) {
          setClickStats(null);
          toast.error("Erreur lors du chargement des statistiques de connexion");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetch();
    return () => { ignore = true; };
  }, [selectedPeriod, customFromDate, customToDate, selectedUserType, selectedAccountStatus]);

  const hourlyData: HourlyChartPoint[] = (clickStats?.hourly ?? []).map((h) => ({
    intervalle: h.label,
    connectes:  h.logins,
  }));

  const loginClicks  = clickStats?.loginClicks  ?? 0;
  const logoutClicks = clickStats?.logoutClicks ?? 0;
  const peak = Math.max(loginClicks + logoutClicks, 1);

  const peakHour = (clickStats?.hourly ?? []).reduce<{ label: string; logins: number } | undefined>(
    (max, h) => (!max || h.logins > max.logins ? h : max),
    undefined,
  );
  const dailyPeak: DailyPeak | undefined = peakHour
    ? { date: peakHour.label, connected: peakHour.logins }
    : undefined;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="ccsp-panel-grid">

      <div className="ccsp-card--daily">
        <div className="ccsp-card-header">
          <h3 className="ccsp-card-title">
            Connexions et déconnexions journalières
          </h3>
          <p className="ccsp-card-subtitle">
            Nombre de clics sur &laquo;&nbsp;Se connecter&nbsp;&raquo; et
            &laquo;&nbsp;Se déconnecter&nbsp;&raquo; enregistrés sur la période.
          </p>
        </div>

        <div className="ccsp-gauges-row">
          <DonutGauge
            value={loginClicks}
            peak={peak}
            fillColor={RECHARTS_COLORS.teal}
            trackColor={RECHARTS_COLORS.trackTeal}
            variant="teal"
            label="Connexions"
            sublabel="clics sur Se connecter"
          />

          <div className="ccsp-gauges-divider" />

          <DonutGauge
            value={logoutClicks}
            peak={peak}
            fillColor={RECHARTS_COLORS.orange}
            trackColor={RECHARTS_COLORS.trackOrange}
            variant="orange"
            label="Déconnexions"
            sublabel="clics sur Se déconnecter"
          />
        </div>

        <div className="ccsp-summary-strip">
          <span className="ccsp-summary-strip__label">Total clics sur la période :</span>
          <span className="ccsp-summary-strip__value">
            {(loginClicks + logoutClicks).toLocaleString("fr-FR")}
          </span>
        </div>
      </div>

      <HourlyConnectionChart
        data={hourlyData}
        selectedPeriod={selectedPeriod}
        dailyPeak={dailyPeak}
      />

    </div>
  );
}